import { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { FaGasPump } from "react-icons/fa6";
import { BsCart2 } from "react-icons/bs";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../pages/auth/authSlice";
import { useLogoutUserMutation } from "../../features/api/authApiSlice";
import { apiSlice } from "../../app/api/apiSlice";
import "./Navbar.scss";
import CartDrawer from "../cart/CartDrawer";
import { toggleCartDrawer } from "../../features/api/cartSlice";

// 🔌 Import your live backend data query hook
import { useGetCartQuery } from "../../features/api/cartApiSlice";
import useGuestCart from "../../hook/useGuestCart";

const Navbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutUser] = useLogoutUserMutation();

  const { user, accessToken, authChecked } = useSelector((state) => state.auth);

  const isLoggedIn = Boolean(accessToken);

  // UI sliding state tracker flag remain local
  const cartOpenFlag = useSelector((state) => state.cart?.drawerOpen || false);

  // 🔥 THE FRONTEND FIX: Read directly from your live server query hooks stream
  // Registered customers: MongoDB cart
  const { data: cartData } = useGetCartQuery(undefined, {
    skip: !authChecked || !isLoggedIn,
  });

  // Guest customers: Browser localStorage cart
  const guestCart = useGuestCart();

  // Select the appropriate cart
  const cartItems = authChecked
    ? isLoggedIn
      ? cartData?.items || []
      : guestCart.items
    : [];

  // Calculate total quantity for navbar badge
  const totalCartItemCount = cartItems.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );

  const closeMenu = () => setMenuOpen(false);

  const handleLogout = async () => {
    dispatch(logout());
    dispatch(apiSlice.util.resetApiState());
    closeMenu();
    navigate("/", { replace: true });
    try {
      await logoutUser().unwrap();
    } catch (error) {
      console.error("Logout request failed:", error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const checkActiveLink = ({ isActive }) =>
    isActive ? "navbar__link active" : "navbar__link";
  const checkActiveMobileLink = ({ isActive }) =>
    isActive ? "mobile-menu__link active" : "mobile-menu__link";

  return (
    <>
      <header
        className={`navbar ${scrolled ? "navbar--scrolled" : ""} ${
          menuOpen ? "navbar--open" : ""
        }`}
      >
        <Link to="/" className="navbar__logo" onClick={closeMenu}>
          <img
            src="https://sunrisebucket.s3.us-east-2.amazonaws.com/logo.PNG"
            alt="Mini Miles Adventures"
          />
        </Link>

        <nav className="navbar__links">
          {/* <NavLink to="/journeys" className={checkActiveLink}>
            {" "}
            Journeys{" "}
          </NavLink> */}
          <NavLink to="/" className={checkActiveLink}>
            {" "}
            Home{" "}
          </NavLink>
          <NavLink to="/watch" className={checkActiveLink}>
            {" "}
            Watch{" "}
          </NavLink>
          <NavLink to="/community-map" className={checkActiveLink}>
            {" "}
            Community Map{" "}
          </NavLink>
          <NavLink to="/shop" className={checkActiveLink}>
            {" "}
            Shop{" "}
          </NavLink>
          <NavLink to="/roadnotes" className={checkActiveLink}>
            {" "}
            Road Notes{" "}
          </NavLink>
          <NavLink to="/gallery" className={checkActiveLink}>
            {" "}
            Gallery{" "}
          </NavLink>
          <NavLink to="/about" className={checkActiveLink}>
            {" "}
            About{" "}
          </NavLink>
          <NavLink to="/contact" className={checkActiveLink}>
            {" "}
            Contact{" "}
          </NavLink>
          {/* <NavLink to="/blog" className={checkActiveLink}>
            {" "}
            Blog{" "}
          </NavLink> */}
          {isLoggedIn ? (
            <>
              {user?.roles?.includes("admin") && (
                <NavLink to="/admin/dashboard" className={checkActiveLink}>
                  {" "}
                  Admin{" "}
                </NavLink>
              )}
              <button
                type="button"
                className="navbar__logout-btn"
                onClick={handleLogout}
              >
                {" "}
                Logout{" "}
              </button>
              <NavLink to="/dashboard" className={checkActiveLink}>
                {" "}
                Dashboard{" "}
              </NavLink>
            </>
          ) : (
            <>
              <NavLink to="/login" className={checkActiveLink}>
                {" "}
                Login{" "}
              </NavLink>
              <NavLink to="/register" className={checkActiveLink}>
                {" "}
                Register{" "}
              </NavLink>
            </>
          )}
          {/* 🛒 Live Desktop Counter Badge */}
          <button
            type="button"
            className="navbar__cart-trigger-btn"
            onClick={() => dispatch(toggleCartDrawer())}
            aria-label="Open shopping cart drawer"
          >
            <BsCart2 />
            {totalCartItemCount > 0 && (
              <span className="cart-badge-counter-bubble">
                {" "}
                {totalCartItemCount}{" "}
              </span>
            )}
          </button>
          <Link to="/fuel-up" className="navbar__fuel-btn">
            <span>
              {" "}
              <FaGasPump />{" "}
            </span>{" "}
            Fuel the Car
          </Link>
        </nav>

        <div className="navbar__mobile-actions-wrapper">
          {/* 🛒 Live Mobile Counter Badge */}
          <button
            type="button"
            className="navbar__cart-trigger-btn navbar__cart-trigger-btn--mobile"
            onClick={() => {
              closeMenu();
              dispatch(toggleCartDrawer());
            }}
            aria-label="Open shopping cart drawer"
          >
            <BsCart2 />
            {totalCartItemCount > 0 && (
              <span className="cart-badge-counter-bubble">
                {" "}
                {totalCartItemCount}{" "}
              </span>
            )}
          </button>

          <button
            type="button"
            className={`navbar__toggle ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle navigation menu"
          >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>
        </div>

        <div className={`mobile-menu ${menuOpen ? "open" : ""}`}>
          <nav className="mobile-menu__nav">
            <NavLink
              to="/journeys"
              className={checkActiveMobileLink}
              onClick={closeMenu}
            >
              {" "}
              Journeys{" "}
            </NavLink>
            <NavLink
              to="/watch"
              className={checkActiveMobileLink}
              onClick={closeMenu}
            >
              {" "}
              Watch{" "}
            </NavLink>
            <NavLink
              to="/roadnotes"
              className={checkActiveMobileLink}
              onClick={closeMenu}
            >
              {" "}
              Road Notes{" "}
            </NavLink>
            <NavLink
              to="/gallery"
              className={checkActiveMobileLink}
              onClick={closeMenu}
            >
              {" "}
              Gallery{" "}
            </NavLink>
            <NavLink
              to="/shop"
              className={checkActiveMobileLink}
              onClick={closeMenu}
            >
              {" "}
              Shop{" "}
            </NavLink>
            <NavLink
              to="/about"
              className={checkActiveMobileLink}
              onClick={closeMenu}
            >
              {" "}
              About{" "}
            </NavLink>
            <NavLink
              to="/contact"
              className={checkActiveMobileLink}
              onClick={closeMenu}
            >
              {" "}
              Contact{" "}
            </NavLink>
            {/* <NavLink
              to="/blog"
              className={checkActiveMobileLink}
              onClick={closeMenu}
            >
              {" "}
              Blog{" "}
            </NavLink> */}
            {isLoggedIn ? (
              <>
                {user?.roles?.includes("admin") && (
                  <NavLink
                    to="/admin"
                    className={checkActiveMobileLink}
                    onClick={closeMenu}
                  >
                    {" "}
                    Admin{" "}
                  </NavLink>
                )}
                <button
                  type="button"
                  className="mobile-menu__logout"
                  onClick={handleLogout}
                >
                  {" "}
                  Logout{" "}
                </button>
              </>
            ) : (
              <>
                <NavLink
                  to="/login"
                  className={checkActiveMobileLink}
                  onClick={closeMenu}
                >
                  {" "}
                  Login{" "}
                </NavLink>
                <NavLink
                  to="/register"
                  className={checkActiveMobileLink}
                  onClick={closeMenu}
                >
                  {" "}
                  Register{" "}
                </NavLink>
              </>
            )}
            <Link
              to="/fuel-up"
              className="mobile-menu__fuel-link"
              onClick={closeMenu}
            >
              {" "}
              <FaGasPump /> Fuel the Car{" "}
            </Link>
          </nav>
        </div>
      </header>

      {cartOpenFlag && (
        <CartDrawer closeDrawer={() => dispatch(toggleCartDrawer())} />
      )}
    </>
  );
};

export default Navbar;
